import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check, Mail, Loader2, ArrowRight } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDateTime } from "@/lib/format";

const PRIORITY_DOT = { urgent: "var(--mmm-red)", high: "#B7791F", normal: "#9BB9CE", low: "#CBD5E1" };

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [digestBusy, setDigestBusy] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/center/summary");
      setUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/center", { params: { limit: 15 } });
      setItems(data.items || []);
      loadCount();
    } catch (e) { /* ignore */ }
  }, [loadCount]);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);

  const markAll = async () => { try { await api.post("/notifications/mark-read", { all: true }); } catch (e) {} loadList(); };
  const emailDigest = async () => {
    setDigestBusy(true);
    try {
      const { data: r } = await api.post("/ext-digest/send-now");
      if (r.sent) { toast.success("Your external referral digest has been queued."); loadList(); }
      else toast.info("No external referral items need attention right now.");
    } catch { toast.error("Could not send your digest. Please try again."); }
    finally { setDigestBusy(false); }
  };
  const open = async (n) => {
    if (!n.read) { try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch (e) {} }
    const link = n.related_url || n.link;
    if (link) navigate(link);
    loadList();
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && loadList()}>
      <DropdownMenuTrigger asChild>
        <button className="relative h-9 w-9 rounded-full flex items-center justify-center hover:bg-[rgba(28,67,88,0.06)] transition-colors" data-testid="notification-bell" aria-label="Notifications">
          <Bell size={18} className="text-[#1C4358]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
              style={{ backgroundColor: "var(--mmm-red)" }} data-testid="notification-count">{unread > 9 ? "9+" : unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#E6EDF2]">
          <span className="font-semibold text-sm text-[#252221]">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#1C4358] hover:underline flex items-center gap-1" data-testid="mark-all-read">
              <Check size={13} /> Mark all read
            </button>
          )}
        </div>
        <button onClick={emailDigest} disabled={digestBusy} data-testid="topbar-digest-btn"
          className="w-full flex items-center gap-2 px-3 py-2 border-b border-[#E6EDF2] text-[13px] font-medium text-[#1C4358] hover:bg-[rgba(28,67,88,0.06)] transition-colors disabled:opacity-60">
          {digestBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          Email my external referral digest
        </button>
        <div className="max-h-80 overflow-y-auto mmm-scroll">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">You're all caught up.</p>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid="notification-item"
              className={`w-full text-left px-3 py-2.5 border-b border-[#F0F4F7] last:border-0 hover:bg-[rgba(155,185,206,0.10)] ${!n.read ? "bg-[rgba(155,185,206,0.07)]" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: PRIORITY_DOT[n.priority] || "var(--mmm-red)" }} />}
                <div className={`min-w-0 ${n.read ? "pl-4" : ""}`}>
                  <div className="text-sm font-medium text-[#252221]">{n.title}</div>
                  {(n.body || n.message) && <div className="text-xs text-muted-foreground">{n.body || n.message}</div>}
                  <div className="text-[11px] text-muted-foreground mt-0.5">{formatDateTime(n.created_at)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
        <button onClick={() => navigate("/notifications")} data-testid="notification-view-all"
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2.5 border-t border-[#E6EDF2] text-[13px] font-medium text-[#1C4358] hover:bg-[rgba(28,67,88,0.06)] transition-colors">
          View all notifications <ArrowRight size={14} />
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
